import { createSlice } from "@reduxjs/toolkit";
import { normalizeJourneyDate, todayIso } from "../../services/journeyDate";

/** Gornja granica jedne kupovine — grupe preko ovoga idu preko prodavca. */
export const MAX_PASSENGERS = 20;

const initialState = {
    departureStation: null,
    arrivalStation: null,
    journeyDate: todayIso(),
    journeyDateAdjusted: false,
    // Da li je korisnik SAM izabrao datum — samo tada zaslužuje obaveštenje
    // kad taj datum pri rehidraciji istekne. Podrazumevani „danas" od juče
    // se pomera tiho, inače svakodnevni posetilac gleda poruku svaki dan.
    journeyDateTouched: false,
    returnDate: null,
    tripType: "oneWay",
    passengers: 1,
    tripResults: [],
    selectedTrip: null,
    isLoading: false,
    error: null,
};

const searchSlice = createSlice({
    name: "search",
    initialState,
    reducers: {
        setDepartureStation(state, action) {
            state.departureStation = action.payload;
        },
        setArrivalStation(state, action) {
            state.arrivalStation = action.payload;
        },
        // Programske korekcije (clamp na danas, sinhronizacija) — ne diraju touched.
        setJourneyDate(state, action) {
            state.journeyDate = action.payload;
        },
        // Izbor iz date picker-a / trake dana — korisnikova svesna odluka.
        pickJourneyDate(state, action) {
            state.journeyDate = action.payload;
            state.journeyDateTouched = true;
        },
        setReturnDate(state, action) {
            state.returnDate = action.payload;
        },
        setTripType(state, action) {
            state.tripType = action.payload;
        },
        setPassengers(state, action) {
            // Ograničenje stoji ovde, a ne samo na polju: u broj se može i ukucati,
            // a svaki putnik zauzima sedište u rezervaciji.
            const wanted = parseInt(action.payload, 10);
            state.passengers = Number.isFinite(wanted)
                ? Math.min(MAX_PASSENGERS, Math.max(1, wanted))
                : 1;
        },
        setTripResults(state, action) {
            state.tripResults = action.payload;
        },
        setSelectedTrip(state, action) {
            state.selectedTrip = action.payload;
        },
        setLoading(state, action) {
            state.isLoading = action.payload;
        },
        setError(state, action) {
            state.error = action.payload;
        },
        resetSearch(state) {
            return initialState;
        },
        clearJourneyDateAdjusted(state) {
            state.journeyDateAdjusted = false;
            // Posle pomeranja datum je opet „danas", ne korisnikov izbor.
            state.journeyDateTouched = false;
        },
    },
    // Pri rehidraciji persistovanog journeyDate-a datum iz prošlosti se
    // pomera na danas; flag pali jednokratno obaveštenje u formi pretrage.
    // Reducer menja polje TOKOM REHYDRATE, pa autoMergeLevel1 zadržava našu
    // (normalizovanu) vrednost umesto sirove persistovane.
    extraReducers: (builder) => {
        builder.addMatcher(
            (action) => action.type === "persist/REHYDRATE" && action.key === "search",
            (state, action) => {
                const { date, adjusted } = normalizeJourneyDate(action.payload?.journeyDate);
                state.journeyDate = date;
                // Obaveštenje samo ako je istekli datum korisnik lično birao.
                state.journeyDateAdjusted =
                    adjusted && action.payload?.journeyDateTouched === true;
            }
        );
    },
});

export const {
    setDepartureStation,
    setArrivalStation,
    setJourneyDate,
    pickJourneyDate,
    setReturnDate,
    setTripType,
    setPassengers,
    setTripResults,
    setSelectedTrip,
    setLoading,
    setError,
    resetSearch,
    clearJourneyDateAdjusted,
} = searchSlice.actions;

export default searchSlice.reducer;
